
import { Fragment, useEffect, useRef, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon/FuseSvgIcon';
import _ from '@lodash';
import FuseUtils from '@fuse/utils';
import { authPermissions } from 'app/auth';
import { useSelector, useDispatch } from 'react-redux';
import { AuraSimpleTableHead } from 'app/main/apps/common/AuraSimpleTable';
import { getStatuses } from 'app/main/dashboards/main/store/widgetsSlice';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import {
  Checkbox,
  Collapse,
  Icon,
  IconButton,
  TextField,
  Box,
  Button,
  Tooltip,
  Chip,
  Alert, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { openDialog, closeDialog as fuseCloseDialog } from 'app/store/fuse/dialogSlice';
import AuraControlledAutocomplete from 'app/main/apps/common/AuraControlledAutocomplete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Typography from '@mui/material/Typography';
import OrdersRowActions from 'app/main/apps/e-commerce/orders/OrdersRowActions';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useReactToPrint } from 'react-to-print';
import { Loader } from 'app/main/apps/common/ListInfoMessage';
import { isCustomerApprovalPopupStage, popupInlineInput } from 'app/main/apps/common/popupActions';
import { getSuppliers, getSupplierRoundHandoff, quoteToOrder, quoteToSupplier, receiveSupplierShipmentOrder } from 'app/main/apps/e-commerce/store/rfqsSlice';
import { getOrders, getPaymentHistory } from 'app/main/apps/e-commerce/store/ordersSlice';
import PaymentReversalDialog from './PaymentReversalDialog';
import isPaymentHistoryView, { canReverseRow } from './paymentReversalScope';
import { showMessage } from 'app/store/fuse/messageSlice';
import { formatDateString, getDiscountAmtFromPercentage, isCustomer, isSupplier, formatter, isAdminOrStaff, formatWeight, calculateAndFormatTotalWeight, displayName, isValidObject, checkNullOrUndefined, isVasTeam, isStaffWithoutPriceView, canViewFile, handleDownload, onFileViwerClick } from 'app/main/apps/common/AuraFunctions';
import { isRepeatSupplierRound, supplierNoteFieldCopy } from 'app/main/apps/e-commerce/order/supplierRoundNote';
import ImageRenderer, { SimpleCardDocumentsViewer } from 'app/main/apps/common/ImageRenderer';
import ProductDetailBody, { ProductTableHeader } from 'app/main/apps/e-commerce/order/Dialogs/ProductDetailTable';
import { receiveShipment } from 'app/main/apps/backoffice/calendar/store/eventsSlice';
import { openVasPreview } from 'app/main/apps/e-commerce/order/Vas/VasHelper';
import VasPriceList from 'app/main/apps/e-commerce/order/Vas/VasPriceList';
import AuraAccordion from 'app/main/apps/common/AuraAccordion';
import {
  orderDetailsContainNonCustomProduct,
  SUPPLIER_QUOTE_REQUIRES_CUSTOM_MESSAGE,
} from 'app/utils/requestForQuoteValidation';
import { closeViewOrderDialog } from '../store/widgetsSlice';
import { closeViewOrderDialog as orderCloseViewOrderDialog, placeReOrder, resetOrder, getBriefInvoiceDetails } from '../../../apps/e-commerce/store/orderSlice';
import OrderHeader from '../../../apps/common/OrderHeader';
import AuraBaseDialog from '../../../apps/common/AuraBaseDialog';
import InvoiceDiscrepancyPanel from '../../../apps/e-commerce/order/Dialogs/InvoiceDiscrepancyPanel';
import OpenOrdersRowAction from '../widgets/OpenOrdersRowAction';
import ImageContent from './ImageContent';
import PrintableColumns from '../print/PrintableColumns';
import ApprovalContent from '../../../apps/e-commerce/order/Dialogs/ApprovalContent';
import { resolveFileSrc } from 'app/main/apps/common/fileSource';

const editInput = {
  minHeight: '35px',
  "& .MuiOutlinedInput-input": {
    padding: "4px !important",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#bcccdc !important",
    },
  },
};

const accordionStyle = {
  root: {
    borderRadius: 2,
    mb: 2,
    boxShadow: "none",
    backgroundColor: "#f1f5f9",
  },
  rootExpanded: {
    backgroundColor: "#fff",
  },
  summary: {
    minHeight: "48px !important",
    padding: "0px 4px",
  },
  summaryContentExpanded: {
    margin: "0",
  },
  title: {
    color: "#4f46e5",
    fontWeight: "700",
    fontSize: "16px",
    textTransform: "uppercase",
  },
  details: {
    backgroundColor: "#fff",
    padding: "0px",
  },
};

/**
 * PGSS-5318: one round-document action, as a keyboard-operable control.
 *
 * Typography renders a <p>, so role/tabIndex/onKeyDown have to be supplied by hand for this to be anything
 * other than mouse-only. Space is prevented as well as handled — the browser's default for Space is to
 * scroll the dialog, which it would otherwise do on top of activating.
 */
function FileAction({ label, onActivate }) {
  return (
    <Typography
      color="secondary"
      role="button"
      tabIndex={0}
      className="ml-12 cursor-pointer font-medium hover:font-bold text-13"
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate();
        }
      }}
    >
      {label}
    </Typography>
  );
}

export default function ViewOrderDialog(props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(({ user }) => user);
  const isManage = currentUser && FuseUtils.hasPermission(authPermissions.manage, currentUser.role);
  const canViewInvoiceDiscrepancy = currentUser && FuseUtils.hasPermission(authPermissions.invoiceDiscrepancy, currentUser.role);
  const [paymentToReverse, setPaymentToReverse] = useState(null);
  const performingAction = useSelector(({ eCommerceApp }) => eCommerceApp.order.performingAction);
  const [open, setOpen] = useState({});
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  // PGSS-5331 / PGSS-5318: what staff want to tell the SUPPLIER on this send — why they are being asked to
  // quote again. Kept apart from the customer-side revision note on purpose: that one is written about the
  // customer's quote and PGSS-5289 removed the copy that used to put it in front of a supplier. Blank means
  // this round says nothing, and the supplier is shown nothing rather than the previous round's note.
  const [supplierNote, setSupplierNote] = useState('');
  // PGSS-5331: this round's revision documents, as offered by the hand-off endpoint. Every one starts
  // selected — they are the files staff attached for this round — and deselecting one means it is not sent.
  const [roundDocuments, setRoundDocuments] = useState([]);
  const [selectedRoundDocumentIds, setSelectedRoundDocumentIds] = useState([]);
  // PGSS-5318: did this round arrive with anything from Make it In Review? Recorded as the hand-off lands
  // and left alone afterwards — staff clearing the note box is them choosing to send nothing, not the round
  // turning into a first send, and the wording must not flip under them as they type.
  const [hasRoundHandoff, setHasRoundHandoff] = useState(false);
  // PGSS-5318: has the hand-off fetch answered yet? Until it has, this screen cannot tell a first send from
  // a re-quote, so the prompt waits rather than guessing — see supplierNoteFieldCopy. previousSuppliers
  // needs no equivalent: the whole block below renders a loader while suppliersListLoading is true, so it is
  // already settled by first paint.
  const [roundHandoffLoaded, setRoundHandoffLoaded] = useState(false);
  const [isChanged, setIschnaged] = useState(false);
  const [map, setMap] = useState('documents');
  const [selectedIndex, setSelectedIndex] = useState()
  const [openProductDetails, setopenProductDetails] = useState([])
  const [approvalDialog, setApprovalDialog] = useState({})
  const [attachMessage, setAttachMessage] = useState(false);
  const [checked, setChecked] = useState(false)
  const [selectedImages, setSelectedImages] = useState({})
  const [reasonOption, setReasonOption] = useState([])
  const [loading, setLoading] = useState(false)
  const [isOutboundShipments, setIsOutboundShipments] = useState(null);
  const [hideAmountDetails, setHideAmountDetails] = useState(null);
  const [edittableData, setEdittableData] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState({
    revisionDocuments: true,
  });
  const [invoiceDiscrepancy, setInvoiceDiscrepancy] = useState(null);
  // PGSS-5309: after Sync, do not re-seed from the open-time dialog payload.
  const invoiceDiscrepancySeededForIdRef = useRef(null);


  const buildSelectedDrawing = (selectedImagesData, fullDetails) => {
    if (fullDetails?.length === 0) return []
    return fullDetails.map((detail, index) => {
      const selectedDocs = selectedImagesData[index] || [];
      const selectedDocIds = selectedDocs.map(doc => doc.id);
      const filteredDocuments = detail?.drawing_documents?.filter(doc => selectedDocIds.includes(doc.id));
      return {
        order_detail_id: detail.id,
        file: filteredDocuments
      };
    });
  };

  function validateDrawingDocuments(selectedImagesData, fullDetails) {
    if (fullDetails?.length === 0) return []
    const errors = [];
    let isAnyDrawingDocumentsFound = false;
    const arrayIndex = [];
    fullDetails?.forEach((detail, index) => {
      const isDrawingDocumentsFound = detail?.drawing_documents?.length > 0;
      if(!isAnyDrawingDocumentsFound) {
        isAnyDrawingDocumentsFound = isDrawingDocumentsFound;
      }
      if(isDrawingDocumentsFound) {
        const selectedDocs = selectedImagesData[index] || [];
        const selectedDocIds = selectedDocs.map(doc => doc.id);
        const filteredDocuments = detail?.drawing_documents?.filter(doc => selectedDocIds.includes(doc.id));
        const noDocumentSelected = !filteredDocuments || (filteredDocuments && filteredDocuments?.length === 0);
        if (noDocumentSelected) {
          errors.push({
            order_detail: detail,
            row_index: index,
          })
          arrayIndex.push(index+1);
        }
      }
    });
    if(isAnyDrawingDocumentsFound && errors.length > 0) {
      return {errors, errorRows: arrayIndex}
    }
    return {};
  }

  const showDrawingConfirmation = (arrayIndex) => {
    dispatch(
      openDialog({
        children: (
          <>
            <DialogTitle id="alert-dialog-title">
              Drawing/Proof Confirmation
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                No drawing/proof selected for order item {_.join(arrayIndex, ", ")}.
                Do you want to proceed?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  dispatch(fuseCloseDialog());
                  setLoading(false);
                }}
                color="primary"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  dispatch(fuseCloseDialog());
                  sendQuoteRequestToSupplier();
                }}
                color="primary"
                autoFocus
              >
                Confirm
              </Button>
            </DialogActions>
          </>
        ),
      })
    );
  };

  const buildRecieveOrderPayload = (orderId, formValues) => {
    const payload = {
      order_id: orderId,
      order_details: []
    }
    formValues?.order_details?.forEach((item) => {
      if (item?.isSelected) {
        payload.order_details.push({
          id: item?.order_detail_id,
          quantity: item?.newQty,
          notes: _.isObject(item?.notes) ? item?.notes?.inputValue || item?.notes?.name : item?.notes
        })
      }
    })
    return payload
  }

  const handleSelectedImages = (media, index) => {
    setSelectedImages(prevState => {
      // Initialize the state for the given index if it's undefined
      const currentSelected = prevState[index] || [];

      // Check if the media is already selected
      const isAlreadySelected = currentSelected.some(item => item.id === media.id);

      if (isAlreadySelected) {
        // If it's already selected, remove it from the state
        return {
          ...prevState,
          [index]: currentSelected.filter(item => item.id !== media.id)
        };
      }
      // If it's not selected, add it to the state
      return {
        ...prevState,
        [index]: [...currentSelected, media]
      };
    });
  }

  const viewOrderDialog = useSelector(({ mainDashboardApp, eCommerceApp }) => {
    if (props.isDashboard) {
      return mainDashboardApp?.widgets?.viewOrderDialog;
    }
    return eCommerceApp?.order?.viewOrderDialog;
  });

  // PGSS-5326 — reversing a returned payment, same roles that can record one. The view check
  // matters as much as the role check: see paymentReversalScope.
  //
  // PGSS-5541: this MUST stay below viewOrderDialog. It was declared ~170 lines earlier, which
  // read viewOrderDialog before its `const` and threw "Cannot access before initialization" on
  // EVERY render of this component -- not just the payment view -- taking out the RFQ page.
  const canReversePayment = isPaymentHistoryView(viewOrderDialog?.param)
    && currentUser && FuseUtils.hasPermission(authPermissions.paymentReversal, currentUser.role);

  useEffect(() => {
    if (viewOrderDialog.data) {
      viewOrderDialog.data.forEach((orderDetail, index) => {
        setOpen((prev) => ({ ...prev, [index]: true }))
      })
    }
  }, [viewOrderDialog.data])

  const isVasPrice = viewOrderDialog.param?.is_vas_order
  const isCommunicationTab = viewOrderDialog.param?.communicationTab

  const [dialogParam, setDialogParam] = useState({
    type: 'View',
    name: 'viewOrder',
    title: 'View Order',
    hideDialogActions: true,
  });
  const [isPrinting, setIsPrinting] = useState(false);
  const { suppliersList, previousSuppliers, suppliersListLoading } = useSelector(
    (state) => state.eCommerceApp.rfqs
  );
  const quotedSuppliersList = useSelector((state) => state.eCommerceApp?.order?.quotedSuppliersList?.orders);
  const quotedSuppliersListLoading = useSelector((state) => state.eCommerceApp?.order?.quotedSuppliersListLoading);
  const paymentHistoryList = useSelector((state) => state.eCommerceApp?.orders?.paymentHistory);
  const selectedProducts = useSelector(({ eCommerceApp }) => eCommerceApp.products.selectedProducts);
  const shipemntInfo = useSelector(({ mainDashboardApp }) => mainDashboardApp?.widgets?.shipmentInfo)

  const methods = useForm({
    mode: 'onChange',
  });
  const { setValue, watch, control, formState, getValues, reset, handleSubmit, unregister } = methods;
  const { errors } = formState;
  const printRef = useRef();
  const promiseResolveRef = useRef(null);
  const form = watch();

  function disableSubmitButton() {
    if (getType('receive shipment'))
      return false;
    if (getType('create supplier order'))
      return !(isChanged && selectedSuppliers.length && selectedIndex);
    if (viewOrderDialog?.param?.onSubmit)
      return loading;
    return loading || !(isChanged && selectedSuppliers.length);
  }

  useEffect(() => {
    reset({ suppliers: [] })
    setSelectedSuppliers([]);
    // PGSS-5331: a note belongs to the send it was typed for, never to the next order opened in this dialog.
    setSupplierNote('');
    setRoundDocuments([]);
    setSelectedRoundDocumentIds([]);
    setHasRoundHandoff(false);
    setRoundHandoffLoaded(false);
    setIschnaged(false);
    setSelectedIndex()
    setSelectedImages({})
    setAttachMessage(false)
    // PGSS-5331: pre-fill from the round staff just recorded. Without this the note box is useless — this
    // screen shows nothing of what was written on Make it In Review, so an empty box asks staff to remember
    // and retype it. Documents have no other route to the supplier at all.
    if (getType('send to supplier') && viewOrderDialog?.param?.id) {
      dispatch(getSupplierRoundHandoff({ id: viewOrderDialog.param.id }))
        .unwrap()
        .then((handoff) => {
          setSupplierNote(handoff?.note || '');
          const documents = handoff?.documents || [];
          setRoundDocuments(documents);
          // Everything this round carries starts included; deselecting is the deliberate act.
          setSelectedRoundDocumentIds(documents.map((document) => document.id));
          // PGSS-5318: a hand-off with anything in it only exists after Make it In Review, so its arrival
          // is what tells this screen the supplier has seen the order before.
          setHasRoundHandoff(Boolean(handoff?.note) || documents.length > 0);
          setRoundHandoffLoaded(true);
        })
        // The thunk swallows its own errors and resolves {}, so this is belt-and-braces: a hand-off that
        // never answers must still leave a usable box rather than a permanently blank prompt.
        .catch(() => setRoundHandoffLoaded(true));
    }
    if(viewOrderDialog?.param?.pageType === "outbound_shipments" || viewOrderDialog?.param?.pageType === "outbound_shipments_customer"){
      setIsOutboundShipments(true)
    } else {
      setIsOutboundShipments(null);
    }
    if(!checkNullOrUndefined(viewOrderDialog?.param?.hideAmountDetails)){
      setHideAmountDetails(viewOrderDialog?.param?.hideAmountDetails)
    }
    setEdittableData(viewOrderDialog?.param?.edittableData || [])
    if(viewOrderDialog?.param?.resetFormOrderDetails) {
      const dataCopy = _.cloneDeep(viewOrderDialog.data);
      reset({order_details: dataCopy})
    }
    if(viewOrderDialog?.param?.setUpdateOrderDetailsFlag) {
      setValue("update_order_details", true)
    }
  }, [viewOrderDialog.props.open]);

  useEffect(() => {
    if (getType('Receive Order')) {
      dispatch(getStatuses('receive_status'))
      .then((response) => {
        if(!response.error) {
          setReasonOption(response?.payload?.data || [])
        }
      })
      reset({
        order_details: viewOrderDialog.data?.map((item) => {
          return {
            newQty: item.quantity,
            order_detail_id: item.id,
            quantity: item.quantity,
            isSelected: true
          }
        })
      })
    }
  }, [reset, viewOrderDialog.props.open])

  useEffect(() => {
    if (viewOrderDialog.param?.resetForm) {
      reset(viewOrderDialog.param.resetFormData)
    }
  }, [viewOrderDialog.param?.resetForm])

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const { completeOrderData, dashboard } = viewOrderDialog;

  // PGSS-5471: from View Details, Send for Quote Approval and Send for Order Approval run on their first
  // click (OrdersRowActions / OpenOrdersRowAction). What their second view collected is offered here
  // instead: the drawing tick boxes, or the add-documents icon on each line. Only in the plain View
  // Details popup -- the row's own review views already show them.
  const isPlainViewPopup = viewOrderDialog?.param?.type === 'View'
    && ['viewOrder', 'Review'].includes(viewOrderDialog?.param?.name);
  const inlineInput = isPlainViewPopup
    ? popupInlineInput({
      stage: viewOrderDialog?.stage,
      statusCode: completeOrderData?.status?.system_code,
      isCustomerUser: isCustomer(currentUser?.role),
    })
    : null;
  const showsInlineDocuments = inlineInput === 'documents';
  const attachmentIsProof = showsInlineDocuments
    ? completeOrderData?.is_proof_approval
    : viewOrderDialog?.param?.is_proof_approval;

  // Invoice stage: seed discrepancy once per invoice id (view-only banner; actions on Confirm Invoice).
  useEffect(() => {
    const invoiceId = completeOrderData?.id;
    if (
      !viewOrderDialog?.props?.open
      || viewOrderDialog.stage !== 'invoice'
      || !canViewInvoiceDiscrepancy
      || !invoiceId
    ) {
      invoiceDiscrepancySeededForIdRef.current = null;
      setInvoiceDiscrepancy(null);
      return undefined;
    }
    if (invoiceDiscrepancySeededForIdRef.current === invoiceId) {
      return undefined;
    }
    invoiceDiscrepancySeededForIdRef.current = invoiceId;
    const existing = !Array.isArray(viewOrderDialog.data)
      ? viewOrderDialog.data?.discrepancy
      : undefined;
    if (existing != null) {
      setInvoiceDiscrepancy(existing);
      return undefined;
    }
    let cancelled = false;
    dispatch(getBriefInvoiceDetails({ id: invoiceId }))
      .unwrap()
      .then((res) => {
        if (!cancelled) {
          setInvoiceDiscrepancy(res?.discrepancy || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInvoiceDiscrepancy(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    viewOrderDialog?.props?.open,
    viewOrderDialog?.stage,
    completeOrderData?.id,
    canViewInvoiceDiscrepancy,
    dispatch,
  ]);

  const getType = name => viewOrderDialog.param?.name === name
  const image = watch('documents');
  const attachmentsProps = {
    type: 'document',
    category: 'document',
    allowAdd: true,
    addTo: 'rfq',
    allowDelete: true,
    acceptedTypes: 'image/*,.pdf',
    changeStyle: true,
  };

  const hideDiscountField = () => {
    if(isStaffWithoutPriceView(currentUser?.role_view)) return true;
    if (isSupplier(currentUser.role) || (viewOrderDialog?.param?.isSupplierInvoice === true)) return true
    if(hideAmountDetails === true) return true
    return ['rfq', 'rfd', 'completed', 'shipment', 'in-production'].includes(viewOrderDialog?.stage) || isVasTeam(currentUser.role);
  }

  const isColumnHidden = (columnName) => {
    const { stage } = viewOrderDialog;
    switch (columnName) {
      case 'Order No':
        return !['invoice', 'in-production'].includes(stage);
      case 'Weight':
      case 'Total Weight':
        return ['rfq', 'invoice', 'rfd', 'completed'].includes(stage);
      case 'Price':
      case 'Amount':
        return ['rfq', 'rfd', 'completed','container-management'].includes(stage) || isOutboundShipments || isVasTeam(currentUser.role) || hideAmountDetails === true || isStaffWithoutPriceView(currentUser?.role_view);
      case 'Tariff':
      case 'Margin':
        if (isCustomer(currentUser.role)) return true
        if (!isCustomer(currentUser.role)) return true
        return !['quote'].includes(stage)
      case 'Discount':
        if (isStaffWithoutPriceView(currentUser?.role_view)) return true
        if (isSupplier(currentUser.role) || (viewOrderDialog?.param?.isSupplierInvoice === true)) return true
        if (hideAmountDetails === true) return true
        return ['rfd', 'completed', 'shipment', 'in-production'].includes(stage) || isVasTeam(currentUser.role);
      case 'Comments':
        if (!isCustomer(currentUser.role) && !isSupplier(currentUser.role)) return true;
        return !['rfq', 'order', 'quote','approval'].includes(stage)
      case 'Reference No':
      case 'Decedent':
        return isSupplier(currentUser.role) && viewOrderDialog.stage === 'in-production'
      default:
        return false;
    }
  };

  const tableHeaders = [
    { name: '' },
    { name: 'No ' },
    { name: 'Order No', hide: !['invoice', 'in-production', 'shipment','container-management'].includes(viewOrderDialog.stage) && !isOutboundShipments },
    { name: 'LEN', hide: showNamefield()},
    { name: 'THK', hide: showNamefield() },
    { name: 'HGT', hide: showNamefield() },
    { name: 'Color', hide: showNamefield() },
    { name: 'Shape', hide: showNamefield() },
    { name: 'Polish', hide: showNamefield() },
    { name: 'Customer Name', hide: !showCustomerNamefield() },
    { name: 'Product Name', hide: !showNamefield() },
    { name: 'Sub Type' },
    { name: 'Order Type', hide: !isOutboundShipments },
    { name: 'Order Type', hide: !showOrderTypefield() },
    { name: 'Reference No', hide: isColumnHidden('Reference No')},
    { name: 'Decedent', hide:  isColumnHidden('Decedent') },
    { name: 'Comments', hide: isColumnHidden('Comments') },
    { name: 'Quantity' },
    { name: `${displayName?.weight}`, hide: isColumnHidden('Weight') },
    { name: 'Tariff', hide: isColumnHidden('Tariff') },
    { name: 'Margin', hide: isColumnHidden('Margin') },
    { name: 'Price', hide: isColumnHidden('Price') || isVasPrice  },
    { name: `Total ${displayName?.weight}`, hide: isColumnHidden('Total Weight') },
    { name: 'Discount', hide: hideDiscountField()},
    { name: 'Amount', hide: isColumnHidden('Amount') },
    { name: 'Box Number', hide: !['in-production', 'fulfilled', 'invoice','container-management'].includes(viewOrderDialog.stage) },

    // { name: 'Amount' },
  ];
  
  // TODO This is a temporary fix that need to be removed once invoice response has been added
  const oldTableHeaders = [
    { name: '' },
    { name: 'No' },
    { name: 'Order No', hide: !['invoice', 'in-production'].includes(viewOrderDialog.stage) },
    { name: 'Product Name' },
    { name: 'Sub Type' },
    { name: 'Reference No' },
    { name: 'Decedent' },
    { name: 'Weight', hide: viewOrderDialog.stage === 'invoice' },
    { name: 'Quantity' },
    { name: 'Price', hide: hideAmountDetails || isStaffWithoutPriceView(currentUser?.role_view) },
    { name: 'Discount', hide: hideDiscountField()},
    { name: 'Amount', hide: isStaffWithoutPriceView(currentUser?.role_view) },
    { name: 'Box Number', hide: ['invoice'].includes(viewOrderDialog.stage) && !isSupplier(currentUser.role) },
  ];

  const hasImages = (type) => viewOrderDialog?.data?.some(d => d[type])
  if (!hasImages('documents')) {
    tableHeaders.shift();
  }

  const supplierHeaders = [{ name: '' }, { name: 'Name' }, { name: 'Email' }, { name: 'Phone' }];
  const chooseSupplierHeaders = [{ name: '' }, { name: 'Name' }, { name: 'Email' }];
  const supplierOrderHeaders = [{ name: '' }, { name: 'Name' }, { name: 'Email' }, { name: 'Phone' }, { name: '' }];
  const shipmentInfoHeaders = [{ name: '' }, { name: 'Order No' }, { name: 'ETA' }, { name: 'Quantity' }, { name: 'Weight' }]
  const receiveOrderHeaders = [
    { name: '' },
    { name: 'No ' },
    { name: 'Product Name' },
    { name: 'Reference No', hide: isColumnHidden('Reference No') },
    { name: 'Decedent', hide: isColumnHidden('Decedent') },
    { name: 'Weight', hide: isColumnHidden('Weight') },
    { name: 'Price', hide: isColumnHidden('Price') || isVasPrice },
    { name: 'Total Weight', hide: isColumnHidden('Total Weight') },
    { name: 'Discount', hide: hideDiscountField() },
    { name: 'Amount', hide: isColumnHidden('Amount') },
    { name: 'Quantity' },
    { name: 'Reason' },
    // { name: 'Notes' },
  ]
  // TODO This is a temporary fix that need to be removed once invoice response has been added
  let headers = tableHeaders;
  if (viewOrderDialog.stage === 'invoice') headers = oldTableHeaders
  if (getType('send to supplier')) headers = isCommunicationTab ? chooseSupplierHeaders : tableHeaders;
  else if (getType('create supplier order')) headers = supplierOrderHeaders;
  else if (getType('Send for Approval')) headers = shipmentInfoHeaders;
  else if (getType('Receive Order')) headers = receiveOrderHeaders
  else if (getType('payment')) headers = [{ name: 'No' }, { name: 'Payment Type' }, { name: 'Amount Received' }, { name: 'Notes' }, { name: 'Date' }, { name: '', hide: !canReversePayment }];

  const getList = () => {
    if (getType('send to supplier')) {
      const filterIds = new Set(previousSuppliers.map(item => item.id));

      // Filter out the objects from the originalArray where the id is in the filterIds set
      return suppliersList.filter(item => !filterIds.has(item.id));
    }
    if (getType('Send for Approval')) {
      return shipemntInfo
    }
    return quotedSuppliersList
  }

  const list = getList();
  const addNote = ['Send for Proof Approval', 'Send for Drawing Approval', 'Approve Drawing', 'Request Revision for Drawing',
    'Reject Drawing', 'Approve Proof', 'Request Revision for Proof', 'Reject', 'Reject Proof', 'REJECT_QUOTE']

  /**
   * Close Dialog
   */
  function closeDialog(event, reason) {
    setLoading(false);
    setInvoiceDiscrepancy(null);
    if (dashboard) {
      dispatch(closeViewOrderDialog());
    } else {
      dispatch(orderCloseViewOrderDialog());
    }
    setopenProductDetails([])
    resetOrder();
  }

  function isDialogName(name) {
    return dialogParam.name === name;
  }

  // PGSS-5148: react-to-print v3 (contentRef) — v2 used findDOMNode, removed in React 19, so print no-op'd.
  const onPrint =
    useReactToPrint({
      contentRef: printRef,
      onBeforePrint: () => {
        return new Promise((resolve) => {
          promiseResolveRef.current = resolve;
          setIsPrinting(true);
        });
      },
      onAfterPrint: () => {
        // Reset the Promise resolve so we can print again
        promiseResolveRef.current = null;
        setIsPrinting(false);
      },
      pageStyle: '',
      documentTitle: `${viewOrderDialog.stage}.pdf`,
  });

  const hideCustomerInHeader = () => {
    return (
      viewOrderDialog.param?.actionName === "RECEIVE_ORDER" ||
      ["in-production"].includes(viewOrderDialog.stage) ||
      (isSupplier(currentUser.role) && viewOrderDialog.stage === "invoice") ||
      viewOrderDialog?.param?.name === "payment" ||
      viewOrderDialog?.param?.hideCustomerName === true
    );
  };

  const showSupplierNameInHeader = () => {
    return (
      ['order'].includes(viewOrderDialog.stage) ||
      (viewOrderDialog?.param?.name === 'payment' && dialogParam?.isSupplierInvoice) ||
      (isOutboundShipments && !isCustomer(currentUser.role))
    );
  };

  const showVasDetails = () => {
    return !isSupplier(currentUser.role) && viewOrderDialog?.data?.length > 0 && viewOrderDialog.data[0].vas && isValidObject(viewOrderDialog.data[0].vas)
  }

  const showVasPriceDetails = () => {
    return showVasDetails() &&
    !isVasTeam(currentUser.role) &&
    !checkNullOrUndefined(viewOrderDialog?.data?.[0]?.vas_price_details) &&
    viewOrderDialog?.data?.[0]?.vas_price_details?.length > 0;
  }

  const showContainerNumber = () => {
    return viewOrderDialog.stage === 'order' && isVasTeam(currentUser.role) && completeOrderData.container_number;
  }

  // The below piece of code is used to show surcharge only for customer and staff and only if surcharge exists
  const showSurchargeAmount = () => {
    return (isAdminOrStaff(currentUser) || isCustomer(currentUser.role)) && (!checkNullOrUndefined(completeOrderData?.sur_charge_amount)) && (completeOrderData?.sur_charge_amount > 0);
  }

  const handleVasPreview = () => {
    const dataCopy = _.cloneDeep(completeOrderData);
    const orderDetails = viewOrderDialog.data?.length > 0 ? _.cloneDeep(viewOrderDialog.data) : null;
    if(orderDetails) {
      dataCopy.order_details = orderDetails;
      dataCopy.proof_documents = orderDetails[0].proof_documents || [];
      dataCopy.production_documents = orderDetails[0].production_documents || [];
      dataCopy.product_proof_documents = orderDetails[0].product_proof_documents || [];
      openVasPreview(dispatch, orderDetails[0].vas, dataCopy)
    }
  };
  const headerData = {
    enable: true,
    component: OrderHeader,
    data: {
      order: completeOrderData,
      hideAmount: getType('Receive Order') || ['invoice'].includes(viewOrderDialog.stage) || isOutboundShipments || isVasTeam(currentUser.role) || isStaffWithoutPriceView(currentUser?.role_view),
      hideQuantity: ['invoice'].includes(viewOrderDialog.stage),
      hideDate: ['invoice', 'rfq', 'rfd', 'completed'].includes(viewOrderDialog.stage) || isOutboundShipments,
      hideCustomer: hideCustomerInHeader(),
      showTotalWeight: (['order', 'quote', 'approval', 'in-production'].includes(viewOrderDialog?.stage) || isOutboundShipments),
      showPrintAction: getType('viewOrder'),
      showDownloadAction: false,
      onPrint,
      stage: viewOrderDialog.stage,
      overRideData: getType('create supplier order') ? {
        amount: _.sumBy(quotedSuppliersList, (supplier) =>
          _.sumBy(supplier.order_details, (item) => item.quantity * item.unit_price)
        )
      } : null,
      hideShipmentDetails: isOutboundShipments,
      showSupplierName: showSupplierNameInHeader(),
      isPrinting,
      hideCTNAndCompany: viewOrderDialog.stage === 'shipment' && ['staff', 'admin', 'sales'].includes(currentUser.role),
      isCommunicationTab,
      pageType: viewOrderDialog?.param?.pageType,
      showShipmentType: ["shipment"].includes(viewOrderDialog.stage) && isOutboundShipments,
      showInvoiceAmounts: viewOrderDialog.stage === 'invoice' && !isSupplier(currentUser.role),
      showVAS: showVasDetails(),
      handleVasPreview,
      showContainerNo: showContainerNumber(),
      hasValidSurcharge: showSurchargeAmount(),
      isContainerManagement: viewOrderDialog?.param?.isContainerManagement === true
    },
  };

  useEffect(() => {
    if (checkNullOrUndefined(viewOrderDialog?.param?.noDocumentsInForm)) {
      setValue('documents', viewOrderDialog?.data?.[0]?.uploadedFile);
    }
    if (viewOrderDialog.param?.showAttachment || showsInlineDocuments) {
      viewOrderDialog?.data?.forEach((item, index) => {
        if (item?.drawing_documents?.length > 0) {
          setValue(`order_details.${index}.drawing_documents`, item.drawing_documents)
        }
      });
    }
  }, [viewOrderDialog]);

  useEffect(() => {
    if (viewOrderDialog?.param?.title === 'Send to Supplier for Vas Price') {
      dispatch(getSuppliers({ suppliers: [], params: { type: 'art/design,vas' } }))
    }

  }, [viewOrderDialog.props.open])

  useEffect(() => {
    if (list?.length && getType('create supplier order')) {
      setSelectedSuppliers([{ id: list[0].supplier.id }]);
      setIschnaged(true)
      setSelectedIndex(list[0].id)
    }
  }, [list, getType('create supplier order')]);

  useEffect(() => {
    if (getType('payment') && viewOrderDialog.props.open && completeOrderData?.id)
      dispatch(getPaymentHistory(completeOrderData?.id))
  }, [getType('payment'), viewOrderDialog])

  const handleSelect = (orderDetail, selected) => {
    if (selected) setSelectedSuppliers((prev) => [...prev, { id: orderDetail.id }]);
    else setSelectedSuppliers((prev) => prev.filter(({ id }) => id !== orderDetail.id));
    setIschnaged(true);
  };

  const handleSendToSupplier = () => {
    if (viewOrderDialog.param?.type === 'reOrder' && selectedSuppliers.length)
      return reOrder()
    if (isCommunicationTab) {
      closeDialog();
      return viewOrderDialog.param?.loadOrdersFun(selectedSuppliers)
    }
    return sendQuoteRequestToSupplier();
  };

  const sendQuoteRequestToSupplier = () => {
    if (orderDetailsContainNonCustomProduct(viewOrderDialog.data)) {
      dispatch(showMessage({ message: SUPPLIER_QUOTE_REQUIRES_CUSTOM_MESSAGE, type: 'error' }));
      return undefined;
    }
    const imageMap = buildSelectedDrawing(selectedImages, viewOrderDialog.data);
    if (loading === false) {
      setLoading(true);
    }
    return dispatch(
      quoteToSupplier({
        id: viewOrderDialog.param.id,
        payload: {
          action_name: viewOrderDialog.param.action,
          suppliers: selectedSuppliers,
          image_map: imageMap,
          // PGSS-5331: sent as its own field. `revision_note` is the customer-side note and must not reach a
          // supplier (PGSS-5289). Trimmed to undefined when empty so the server clears the supplier's note
          // rather than leaving the previous round's on their screen.
          supplier_note: supplierNote?.trim() || undefined,
          // PGSS-5331: the documents staff left ticked. Sent in the shape the server already relays
          // (`selected_revision_documents` → SupplierRevisionRelay), so what the supplier ends up with is
          // exactly what was on screen. Omitted when none are ticked, which sends them none.
          selected_revision_documents: selectedRoundDocumentIds.length
            ? roundDocuments
                .filter((document) => selectedRoundDocumentIds.includes(document.id))
                .map((document) => ({ id: document.id }))
            : undefined,
        },
      })
    )
      .unwrap()
      .then(() => {
        closeDialog();
        if (props.isDashboard && viewOrderDialog.param?.loadOrdersFun)
          viewOrderDialog.param?.loadOrdersFun();
        else dispatch(getOrders({ params: { stage: viewOrderDialog.stage } }));
        dispatch(
          showMessage({
            message:
              viewOrderDialog.stage === 'rfq'
                ? 'Sent to supplier'
                : 'Order has been sent to supplier successfully',
            type: 'success',
          })
        );
      })
      .catch((rejected) => {
        const msg =
          typeof rejected === 'string'
            ? rejected
            : rejected?.message || 'Failed to send to supplier';
        dispatch(showMessage({ message: msg, type: 'error' }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const reOrder = () => {
    const payload = {
      supplier: selectedSuppliers[0],
      order_details: selectedProducts
    }
    dispatch(placeReOrder(payload))
      .then((res) => {
        closeDialog();
        if (!res.error) {
          dispatch(showMessage({ message: 'Success', type: 'success' }));
          props.loadProducts()
        }
      });
  }

  const handleCreateSupplierOrder = () => {
    if (selectedIndex) {
      return dispatch(
        quoteToOrder({ id: viewOrderDialog.param.id, payload: { action_name: viewOrderDialog.param.action, is_approval_type: checked, supplier: selectedSuppliers[0] }, params: { qid: selectedIndex } })
      ).then((res) => {
        closeDialog();
        if (!res.error) {
          dispatch(showMessage({ message: 'Supplier order created', type: 'success' }));
          if (props.isDashboard && viewOrderDialog.param?.loadOrdersFun)
            viewOrderDialog.param?.loadOrdersFun();
          else
            dispatch(getOrders({ params: { stage: 'order' } }))
        }
      });
    }
    setLoading(false);
    return dispatch(showMessage({ message: 'Please Choose Price', type: 'error' }));
  }

  const additionalButton = viewOrderDialog?.param?.showApproveButton &&
    <Button
      variant="contained"
      color="secondary"
      type="submit"
      onClick={
        // handleSubmit(() => handleFormSubmit("ORDER_IN_QUEUE_FOR_VAS_PROOF"))
        handleSubmit(() => handleFormSubmit("ORDER_REQUESTED_FOR_VAS_PROOF"))
      }
    >
      Approve
    </Button>

  useEffect(() => {
    if (getValues()?.order_details?.length) {
      setAttachMessage(false)
    }
  }, [getValues()?.order_details?.length])


  const handleFormSubmit = (type) => {
    const { onSubmit, sendFormValue, showAttachment, title, documentApproval } = viewOrderDialog?.param || {};
    const approvalPayload = [];
    if(getType('receive shipment') || !getType('send to supplier') || (getType('send to supplier') && (viewOrderDialog.param?.type !== 'View' || isCommunicationTab))) {
      setLoading(true);
    }

    if (getType('receive shipment')) {
      if(completeOrderData?.shipment_id) {
        dispatch(receiveShipment({ id: completeOrderData?.shipment_id }))
        .unwrap()
        .then((res) => {
          closeDialog();
          if (!res.error) {
            dispatch(showMessage({ message: 'Shipment received successfully', type: 'success' }));
            dispatch(getOrders({ params: { stage: 'container' } }))
          }
        });
        return;
      }
      setLoading(false);
      return;
    }

    let hasError
    if (showAttachment && title !== 'Move to Production') {
      viewOrderDialog?.data?.forEach((item, index) => {
        const documentData = getValues()?.order_details?.[index]
        const isDocumentExist = viewOrderDialog.param.is_proof_approval ? documentData?.proof_documents?.length > 0 : documentData?.drawing_documents?.length > 0
        const documentKey = viewOrderDialog.param.is_proof_approval ? documentData?.new_proof_documents?.length > 0 : documentData?.new_drawing_documents?.length > 0
        if (!isDocumentExist && !documentKey && title !== 'Send for Order Approval') {
          hasError = true;
        }
      });
    }
    if (documentApproval) {
      viewOrderDialog?.data?.forEach((item, index) => {
        const documentData = getValues()?.order_details?.[index]
        const order = {
          id: item.id,
          is_approval_type: item.is_approval_type,
          documents: []
        };

        if (item.is_approval_type && documentData?.approval_documents === undefined) {
          hasError = true
        }

        if (documentData?.approval_documents?.length > 0) {
          order.documents = documentData?.approval_documents?.map((doc => ({ id: doc.id })));
        }

        approvalPayload.push(order)
      });
    }
    if (hasError) {
      setAttachMessage(true);
      setLoading(false);
      return
    }
    if (getType('Receive Order')) {
      const values = getValues();
      const receiveOrderPayload = buildRecieveOrderPayload(completeOrderData?.id, values)
      dispatch(
        receiveSupplierShipmentOrder({ payload: receiveOrderPayload })
      )
        .unwrap()
        .then(() => {
          closeDialog();
          if (props.isDashboard && viewOrderDialog.param?.loadOrdersFun)
            viewOrderDialog.param?.loadOrdersFun();
          else
            dispatch(getOrders({ params: { stage: viewOrderDialog.stage } }))
          dispatch(showMessage({ message: "Success" }));
        });
      return
    }
    if (onSubmit) {
      const values = getValues();
      const noteValue = sendFormValue ? values : values.note;
      // const document = values?.new_documents?.map(item => ({ id: item.id }))

      onSubmit(noteValue, type, approvalPayload)?.finally(() => {
        closeDialog();
      });
    }
    else if (getType('send to supplier') && viewOrderDialog.param?.type === 'View' && !isCommunicationTab) {
      const validationResponse = validateDrawingDocuments(selectedImages, viewOrderDialog.data);
      if(validationResponse && validationResponse.errorRows && validationResponse.errorRows.length > 0) {
        showDrawingConfirmation(validationResponse.errorRows);
      } else {
        sendQuoteRequestToSupplier();
      }
    }
    else if (getType('send to supplier')) {
      handleSendToSupplier();
    }
    else {
      handleCreateSupplierOrder();
    }
  }

  const handleOpenProductDetials = (index, isOpened) => {
    setopenProductDetails(prev => {
      if (isOpened)
        return prev.filter(id => id !== index)
      return [...prev, index]
    })
  }

  const getMessage = (index, isProofApproval) => {
    const documentData = getValues()?.order_details?.[index]
    const documentKey = isProofApproval ? (documentData?.new_proof_documents || documentData?.proof_documents) : (documentData?.new_drawing_documents || documentData?.drawing_documents)
    if (!documentKey) {
      return 'Document needs to be attached'
    }
    return ''
  }

  const supplierCols = (
    <>
      {list?.map((data, index) => {
        const orderDetail = (getType('create supplier order')) ? data.supplier : data;
        const isOpened = openProductDetails.includes(index)
        let drawingDocuments = [];
        const documentData = getValues()?.order_details?.[index]
        const approvalDocuments = documentData?.approval_documents || []

        if (approvalDocuments?.length > 0) {
          drawingDocuments = approvalDocuments
        }
        return (
          <Fragment key={index}>
            <TableRow>
              <TableCell scope="row" padding='checkbox'>
                {getType('send to supplier') && viewOrderDialog.param?.type !== 'reOrder' ?
                  <Checkbox
                    disabled={previousSuppliers.some(({ id }) => orderDetail.id === id)}
                    onClick={(e) => handleSelect(orderDetail, e.target.checked)}
                    checked={previousSuppliers.some(({ id }) => orderDetail.id === id) || selectedSuppliers.some(({ id }) => orderDetail.id === id)}
                  />
                  : null}
              </TableCell>
              {!getType('Send for Approval') && (
                <>
                  <TableCell scope="row">
                    <Typography className="">{orderDetail?.name}</Typography>
                  </TableCell>
                  <TableCell scope="row">
                    <Typography className="">{orderDetail?.email}</Typography>
                  </TableCell>
                  {
                    !isCommunicationTab && (
                      <TableCell scope="row">
                        <Typography className="">{orderDetail.primary_phone}</Typography>
                      </TableCell>
                    )
                  }
                </>
              )}
              {getType('Send for Approval') && (
                <>

                  <TableCell scope="row">
                    <Typography className="">{orderDetail.order_no}</Typography>
                  </TableCell>
                  <TableCell scope="row">
                    <Typography className="">{formatDateString(orderDetail.eta_date)}</Typography>
                  </TableCell>
                  <TableCell scope="row">
                    <Typography className="">{orderDetail.quantity}</Typography>
                  </TableCell>
                  <TableCell scope="row">
                    <Typography className=""> {formatWeight(orderDetail.weight)}</Typography>
                  </TableCell>
                </>
              )}
              {
                viewOrderDialog.param?.documentApproval &&
                <TableCell scope="row">
                  <Tooltip title="Add Documents">
                    <FuseSvgIcon
                      className="text-48 cursor-pointer"
                      size={24}
                      color="action"
                      onClick={() => {
                        setApprovalDialog({
                          type: 'addAttachments',
                          title: 'Drawing Approval',
                          open: true,
                          attachmentProps: {
                            newAttachmentsType: `order_details.${index}.approval_documents`,

                          }
                        })
                      }}
                    >
                      material-solid:attach_file
                    </FuseSvgIcon>

                  </Tooltip>
                  <Typography className='text-red'>{attachMessage && viewOrderDialog?.data?.[index].is_approval_type && getMessage(index, viewOrderDialog?.data?.[index].is_approval_type)}</Typography>
                </TableCell>

              }
              {(getType('create supplier order') || getType('Send for Approval')) && (
                <>
                  {/* Hided this action and added this logic above while choosing the supplier */}
                  {/* <TableCell scope="row">
                    <div className='flex items-center'>
                      {Number(data.amount) ? <Switch
                        size="small"
                        checked={data.id === selectedIndex}
                        onClick={(e) => setSelectedIndex(prev => prev === data.id ? undefined : data.id)}
                      /> : null}
                      <Typography className="">$ {data.amount}</Typography>
                    </div>
                  </TableCell> */}
                  <TableCell>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleOpenProductDetials(index, isOpened)}
                    >
                      {!isOpened ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                </>)}


            </TableRow>

            {(getType('create supplier order') || getType('Send for Approval')) &&
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                  <Collapse in={getType('Send for Approval') ? isOpened : !isOpened} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 5 }}>
                      <Typography variant="h6" gutterBottom component="div">
                        Product Details
                      </Typography>
                      <Table size="small" aria-label="details">
                        <ProductTableHeader hideCalculativePice type='create supplier order' hideForType={{ vasOrder: data?.vas_order_type, title: viewOrderDialog?.param?.title }} />
                        <ProductDetailBody showDocuments={getType('Send for Approval')} tableData={data.order_details} type='create supplier order' hideForType={{ vasOrder: data?.vas_order_type, title: viewOrderDialog?.param?.title }} />
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>}
            {
              getType('Send for Approval') && drawingDocuments?.length ? (
                <TableRow>
                  <TableCell colSpan={6}>

                    <Typography variant="h6" className="mt-6 mb-10">
                      Approval Documents
                    </Typography>
                    <ImageRenderer attachments={drawingDocuments} />
                  </TableCell>
                </TableRow>
              ) : <></>
            }
          </Fragment>
        )
      })}
    </>
  );

  const paymentCols = (
    <>
      {paymentHistoryList?.map((data, index) => {
        return (
          <TableRow key={index}>
            <TableCell scope="row">
              {index + 1}
            </TableCell>
            <TableCell scope="row">
              <Typography className="">{data.payment_type}</Typography>
            </TableCell>
            <TableCell scope="row">
              <Typography className="">{data.paid_amount !== null && ''}{formatter.format(data.paid_amount)}</Typography>
            </TableCell>
            <TableCell scope="row">
              {
                data?.description ? (
                  <Tooltip title={data.description}>
                    <Typography style={{ whiteSpace: "nowrap" }}>{`${data?.description?.slice(0, 80)}...`}</Typography>
                  </Tooltip>
                ) : '-'
              }
            </TableCell>
            <TableCell scope="row">
              <Typography className="">{formatDateString(data.date)}</Typography>
            </TableCell>
            {canReversePayment && (
              <TableCell scope="row">
                {/* Only a live invoice payment of a positive amount can be returned. An advance
                    payment comes from a different table and is never reversible here. */}
                {canReverseRow(data) ? (
                  <Button size="small" color="secondary" onClick={() => setPaymentToReverse(data)}>
                    Reverse
                  </Button>
                ) : null}
              </TableCell>
            )}
          </TableRow>
        )
      })}
    </>
  )

  const getCols = () => {
    if (getType('create supplier order') || getType('Send for Approval'))
      return supplierCols
    if (getType('payment'))
      return paymentCols

    return null
  }

  let combineAction
  // PGSS-5501: 'approval' is the customer's Waiting for Approval list -- see isCustomerApprovalPopupStage.
  combineAction = isCustomer(currentUser.role) && isCustomerApprovalPopupStage(viewOrderDialog?.stage)

  if (isManage) {
    if (viewOrderDialog?.stage === 'quote' && viewOrderDialog?.param?.name === "Review Quote") {
      combineAction = true
    }
    if (viewOrderDialog?.stage === 'order' && (viewOrderDialog?.param?.uiId === "REVIEW_DRAWING_ORDER" || viewOrderDialog?.param?.uiId === "REVIEW_DRAWING")) {
      combineAction = true
    }
  }

  const getComponent = () => {
    if (props.isDashboard) {
      return (
        <OpenOrdersRowAction
          order={completeOrderData}
          stage={viewOrderDialog.stage}
          orderId={completeOrderData?.id ? completeOrderData?.id : completeOrderData?.shipment_id}
          isManage={isManage}
          orderData={completeOrderData}
          popupButtons
          loadOrders={viewOrderDialog.param.loadOrders}
          closeDialog={closeDialog}
          isDashboard={props.isDashboard}
          type={props.type}
          approveProof={combineAction ? viewOrderDialog?.param?.showDialogActions : undefined}
          approveNote={getValues()?.note}
          listData={viewOrderDialog?.data}
          selectedImages={selectedImages}
          popupData= {{ name: viewOrderDialog?.param?.name, extraPayloadData: _.cloneDeep(getValues()), passExtraPayload: viewOrderDialog?.param?.passExtraPayload, parentParam: viewOrderDialog?.param }}
        />
      )
    }
    return (
      <OrdersRowActions
        row={{ original: { ...completeOrderData } }}
        cell={{ column: { userManage: isManage } }}
        closeDialog={closeDialog}
        popupButtons
        approveProof={combineAction ? viewOrderDialog?.param?.showDialogActions : undefined}
        approveNote={getValues()?.note}
        loadOrders={viewOrderDialog.param.loadOrders}
        listData={viewOrderDialog?.data}
        selectedImages={selectedImages}
        popupData= {{ name: viewOrderDialog?.param?.name, extraPayloadData: _.cloneDeep(getValues()), passExtraPayload: viewOrderDialog?.param?.passExtraPayload, parentParam: viewOrderDialog?.param}}
      />
    )
  }

  const vasCustomzation = () => {
    return (
      <div>
        {viewOrderDialog?.data?.[0]?.vas?.stoneUrl &&
          <div  >
            <svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" >

              <defs>
                <mask id="removeWhiteMask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect x="75" y="75" width="250" height="195" fill="transparent" > </rect>
                </mask>
              </defs>

              <image xlinkHref={resolveFileSrc(viewOrderDialog?.data?.[0]?.vas?.stoneUrl)} width="100%" height="100%" mask="url(#removeWhiteMask)" />
              {viewOrderDialog?.data?.[0]?.vas.images?.map((media, ind) => {
                return <svg xmlns="http://www.w3.org/2000/svg">
                  <image
                    xlinkHref={resolveFileSrc(media)}
                    x={media.x}
                    y={media.y}
                    width={media?.width}
                    height={media?.height}
                    mask="url(#removeWhiteMask)"
                  />
                </svg>
              }
              )}

              {viewOrderDialog?.data?.[0]?.vas.text?.map((labelItem, labelindex) => <text
                textAnchor={labelItem?.align}
                id={labelItem.id}
                x={labelItem.x}
                y={labelItem.y}
                dy=".35em"
                fill="white"
                fontFamily={labelItem.fontGroup}
              >
                {labelItem.value}
              </text>
              )
              }
            </svg>
          </div>
        }
      </div>
    )
  }

  function isShippingCost(orderDetail) {
    return orderDetail?.name === "Shipping Cost" && orderDetail?.order_id === null;
  }

  // This function doesnt add discount 0 default for Surcharge amount
  function isSurchargeCost(orderDetail) {
    return orderDetail?.name === "Additional Surcharge Amount";
  }

  function showCustomerNamefield() {
    return viewOrderDialog?.param?.pageType === "container" && !isCustomer(currentUser.role) ;
  }

  function showOrderTypefield() {
    return viewOrderDialog?.param?.pageType === "container";
  }

  function showNamefield() {
    return viewOrderDialog?.param?.pageType === "container" || isOutboundShipments || isCustomer(currentUser.role) || isSupplier(currentUser.role) || viewOrderDialog?.param?.showNameField;
  }

  /**
   * To check whether that field is edittable
   */
  const isEdittableField = (fieldName) => {
    return _.includes(edittableData, fieldName);
  };

  // TODO This is a temporary fix that need to be removed once invoice response has been added
  const oldColumns = (orderDetail) => (<>
    <TableCell scope="row" sx={{minWidth: "200px", maxWidth: "300px"}} >
      <Typography className="">{orderDetail.name}</Typography>
    </TableCell>
    <TableCell scope="row" sx={{maxWidth: "150px"}}>
      <Typography className="">{orderDetail.sub_type}</Typography>
    </TableCell>
    <TableCell scope="row" sx={{maxWidth: "150px"}}>
      <Typography className="">{orderDetail.customer_provider_number}</Typography>
    </TableCell>
    <TableCell scope="row">
      <Typography className="">{orderDetail.decedents}</Typography>
    </TableCell>
    {((['order', 'quote'].includes(viewOrderDialog.stage)) || getType('Receive Order')) && (
      <TableCell scope="row">
        <Typography className="whitespace-nowrap">{formatWeight(orderDetail.weight)}</Typography>
      </TableCell>
    )}
    <TableCell scope="row">
      <Typography className="">{orderDetail.quantity}</Typography>
    </TableCell>
    {(viewOrderDialog.stage !== 'rfq' && viewOrderDialog.stage !== 'in-production' && !getType('Receive Order')) && (
      <>
        <TableCell scope="row">
          <Typography className="">{orderDetail.unit_price !== null && '$'}{orderDetail.unit_price}</Typography>
        </TableCell>

        {/* This section hides discount for surcharge and shipping cost as line item */}
        {!hideDiscountField() && <TableCell component="th" scope="row">
          {(!isSurchargeCost(orderDetail) && !isShippingCost(orderDetail)) && <Typography className="">
            ${getDiscountAmtFromPercentage(orderDetail?.discount, orderDetail?.unit_price, orderDetail?.quantity)}
          </Typography>}
        </TableCell>}

        <TableCell scope="row">
          <Typography className="">{orderDetail.amount !== null && '$'}{orderDetail.amount}</Typography>
        </TableCell>
        {['invoice'].includes(viewOrderDialog.stage) && isSupplier(currentUser.role) &&
          <TableCell scope="row">
            <Typography>{orderDetail.box_number && orderDetail.box_number.replace(/,/g, ', ')}</Typography>
          </TableCell>}
      </>
    )}
  </>)

  // TODO This is a temporary fix that need to be removed once invoice response has been added
  const newColumns = (orderDetail, index) => {
    const formValues = getValues()?.order_details?.[index] || {}
    const { isSelected, quantity } = formValues || {}
    return (
      <>
        {getType('Receive Order') ? (
          <>
            <TableCell scope="row">
              <Typography className="">{orderDetail.name}</Typography>
            </TableCell>
          </>
          ) : (
          <>
            {showCustomerNamefield() && (
              <TableCell scope="row">
                <Typography className="">{orderDetail.customer_name}</Typography>
              </TableCell>
            )}
            {showNamefield() && (
              <TableCell scope="row" sx={{minWidth: "200px", maxWidth: "300px"}} className='break-words'>
                <Typography>{orderDetail?.name}</Typography>
              </TableCell>
            )}
            {!showNamefield() && (
              <>
                <TableCell scope="row">
                  <Typography className="">{orderDetail.length}</Typography>
                </TableCell>
                <TableCell scope="row">
                  <Typography className="">{orderDetail.width}</Typography>
                </TableCell>
                <TableCell scope="row">
                  <Typography className="">{orderDetail.height}</Typography>
                </TableCell>
                <TableCell scope="row">
                  <Typography style={{ whiteSpace: "nowrap" }}>
                    {orderDetail.color}
                  </Typography>
                </TableCell>
                <TableCell scope="row">
                  {orderDetail?.type?.length > 30 ? (
                    <Tooltip title={orderDetail.type}>
                      <Typography
                        style={{ whiteSpace: "nowrap" }}
                      >{`${orderDetail?.type?.slice(0, 30)}...`}</Typography>
                    </Tooltip>
                  ) : (
                    <Typography style={{ whiteSpace: "nowrap" }}>
                      {" "}
                      {orderDetail?.type}
                    </Typography>
                  )}
                </TableCell>
                <TableCell scope="row">
                  {orderDetail?.polish?.length > 30 ? (
                    <Tooltip title={orderDetail.polish}>
                      <Typography
                        style={{ whiteSpace: "nowrap" }}
                      >{`${orderDetail?.polish?.slice(0, 30)}...`}</Typography>
                    </Tooltip>
                  ) : (
                    <Typography style={{ whiteSpace: "nowrap" }}>
                      {" "}
                      {orderDetail?.polish}
                    </Typography>
                  )}
                </TableCell>
              </>
            )}
            {/* Sub Type section */}
            <TableCell scope="row" sx={isSupplier(currentUser.role) ? { minWidth: "200px", maxWidth: "300px" } : {}} className={isSupplier(currentUser.role) ? 'break-words' : ''}>
              <Typography className="">{orderDetail.subType || orderDetail.sub_type }</Typography>
            </TableCell>
            {/* Order type section */}
            {isOutboundShipments && (
              <TableCell scope="row">
                <Typography className="">{orderDetail.customer_order_type}</Typography>
              </TableCell>
            )}
            {/* Order type section */}
            {showOrderTypefield() && (
              <TableCell scope="row">
                <Typography className="">{orderDetail.order_type}</Typography>
              </TableCell>
            )}
          </>
          )
        }
        {/* Reference No section */}
        {!isColumnHidden('Reference No') &&
          <TableCell scope="row" sx={{maxWidth: "150px"}} className='break-words'>
            {isEdittableField("reference_no") ?
              <Controller
                control={control}
                name={`order_details.${index}.customer_provider_number`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder='Enter here...'
                    label=""
                    id={`customer_provider_number_${index}`}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      sx: { width: "100px", ...editInput },
                    }}
                    sx={{ width: "100px", ...editInput }}
                    value={field?.value}
                    onChange={(e) => {
                      field.onChange(e?.target?.value);
                      setValue(`order_details.${index}.customer_provider_number`, e?.target?.value)
                    }}
                  />
                )}
              />
            :
              <Typography>{orderDetail.customer_provider_number || orderDetail.reference_number}</Typography>
            }
          </TableCell>
        }
        {/* Decendents section */}
        {!isColumnHidden('Decedent') &&
          <TableCell scope="row" sx={{maxWidth: "150px"}} className='break-words'>
            {isEdittableField("decedent") ?
              <Controller
                control={control}
                name={`order_details.${index}.decedent`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder='Enter here...'
                    label=""
                    id={`decedent_${index}`}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      sx: { width: "100px", ...editInput },
                    }}
                    sx={{ width: "100px", ...editInput }}
                    value={field?.value}
                    onChange={(e) => {
                      field.onChange(e?.target?.value);
                      setValue(`order_details.${index}.decedent`, e?.target?.value)
                    }}
                  />
                )}
              />
            :
              <Typography>{orderDetail.decedent || orderDetail.decedents}</Typography>
            }
          </TableCell>
        }
        {/* Comments section */}
        {!isColumnHidden('Comments') &&
          <TableCell component="th" scope="row" sx={{minWidth: "200px", maxWidth: "300px"}} className='break-words'>
            {isEdittableField("comments") ?
              <Controller
                control={control}
                name={`order_details.${index}.comments`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder='Enter here...'
                    label=""
                    id={`comments_${index}`}
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows="1"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      sx: { padding:"4px", width: "180px", ...editInput },
                    }}
                    sx={{ width: "180px", ...editInput }}
                    value={field?.value}
                    onChange={(e) => {
                      field.onChange(e?.target?.value);
                      setValue(`order_details.${index}.comments`, e?.target?.value)
                    }}
                  />
                )}
              />
            :
              <Typography className="">{orderDetail.comments}</Typography>
            }
          </TableCell>
        }
        {/* Quantity section */}
        <TableCell scope="row">
          <Typography className="">{orderDetail.quantity}</Typography>
        </TableCell>
        {/* Weight section */}
        {(!['rfq', 'rfd', 'completed'].includes(viewOrderDialog.stage) || getType('Receive Order')) && (
            <TableCell scope="row">
              <Typography className="whitespace-nowrap">{formatWeight(orderDetail.weight)}</Typography>
            </TableCell>
          )
        }
        {/* Unit Price section */}
        {!isColumnHidden('Price') && !isVasPrice && <TableCell scope="row">
          <Typography className="">{orderDetail.unit_price !== null && ''}{formatter.format(orderDetail.unit_price)}</Typography>
        </TableCell>
        }
        {/* Total Weight section */}
        {!['rfq', 'rfd', 'completed'].includes(viewOrderDialog.stage) &&
          <TableCell scope="row">
            <Typography className="whitespace-nowrap">{calculateAndFormatTotalWeight(orderDetail.quantity, orderDetail.weight)}</Typography>
          </TableCell>
        }
        {/* Discount section */}
        {!hideDiscountField() &&
          <TableCell component="th" scope="row">
            <Typography className="">
            {formatter.format(getDiscountAmtFromPercentage(orderDetail?.discount, orderDetail?.unit_price, orderDetail?.quantity))}
            </Typography>
          </TableCell>
        }
        {/* Amount section */}
        {!isColumnHidden('Amount') &&
          <TableCell scope="row">
            <Typography className="">{(orderDetail.amount !== null || isVasPrice) && ''}{formatter.format(isVasPrice ? orderDetail?.vas_price : orderDetail.amount)}</Typography>
          </TableCell>
        }
        {/* Box Number section */}
        {['in-production', 'fulfilled', 'invoice','container-management'].includes(viewOrderDialog.stage) &&
          <TableCell scope="row" sx={{minWidth: "250px", maxWidth: "250px"}} className='break-words'>
            <Typography>{orderDetail.box_number && orderDetail.box_number.replace(/,/g, ', ')}</Typography>
          </TableCell>
        }
        {/* Document section */}
        {((viewOrderDialog.param?.showAttachment && viewOrderDialog.param?.name !== "Move to Production") || showsInlineDocuments) &&
          <TableCell scope="row">
            <Tooltip title="Add Documents">
              <FuseSvgIcon
                className="custom-icon"
                size={24}
                onClick={() => {
                  setApprovalDialog({
                    type: 'addAttachments',
                    title: attachmentIsProof ? 'Proof Approval' : 'Drawing Approval',
                    open: true,
                    attachmentProps: {
                      attachmentsType: `order_details.${index}.${attachmentIsProof ? 'proof_documents' : 'drawing_documents'}`,
                      newAttachmentsType: `order_details.${index}.${attachmentIsProof ? 'new_proof_documents' : 'new_drawing_documents'}`,
                      payload: { image_customisation_type: attachmentIsProof ? 'proof' : 'drawing' },
                    }
                  })
                }}
              >
                material-solid:attach_file
              </FuseSvgIcon>

            </Tooltip>
            <Typography className='text-red'>{attachMessage && getMessage(index, attachmentIsProof)}</Typography>
          </TableCell>
        }
        {getType('Receive Order') && (
          <>
            <TableCell scope="row">
              <Controller
                control={control}
                name={`order_details.${index}.newQty`}
                rules={{
                  required: {
                    value: true,
                    message: 'Required'
                  },
                  min: {
                    value: 0, // Replace 'min' with the actual minimum prop value
                    message: `Value must be at least ${0}`,
                  },
                  max: {
                    value: quantity, // Replace 'max' with the actual maximum prop value
                    message: `Value cannot exceed ${quantity}`,
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    // className="mb-24"
                    label=""
                    id="schedule"
                    variant="standard"
                    fullWidth
                    size="small"
                    type='number'
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      sx: { width: '60px' }
                    }}
                    sx={{ width: '60px' }}
                    disabled={!isSelected}
                    error={!!errors?.order_details?.[index]?.newQty}
                    helperText={errors?.order_details?.[index]?.newQty?.message}
                    required
                    value={field?.value}
                    onChange={(e) => {
                      //  eslint-disable-next-line
                      setValue(`order_details.${index}.newQty`, parseInt(e?.target?.value))
                      // eslint-disable-next-line
                      if (parseInt(formValues?.quantity) === parseInt(e?.target?.value)) {
                        setValue(`order_details.${index}.notes`, '')
                      }
                      field.onChange(e?.target?.value);
                    }}
                  />
                )}
              />
            </TableCell>
            <TableCell>
              <AuraControlledAutocomplete
                // labelName='Reason'
                control={control}
                rules={{
                  required: {
                    // eslint-disable-next-line
                    value: parseInt(formValues?.quantity || 0) !== parseInt(formValues?.newQty || 0),
                    message: 'Required'
                  }
                }}
                controlName={`order_details.${index}.notes`}
                inputValues={reasonOption || []}
                // eslint-disable-next-line
                required={parseInt(formValues?.quantity) !== parseInt(formValues?.newQty)}
                error={isSelected && !!errors?.order_details?.[index]?.notes}
                helperText={isSelected && errors?.order_details?.[index]?.notes?.message}
                className= 'mt-8'
                // className='mt-0 mb-0'
                defaultValue={formValues?.notes}
                // eslint-disable-next-line
                disabled={parseInt(formValues?.quantity || 0) === parseInt(formValues?.newQty || 0)}
                variant="standard"
                InputProps={{
                  sx: { width: '140px' }
                }}
                creatable
                showAdd
                forcePopupIcon
                freeSolo={false}
                textFieldSx={{ width: '140px' }}
              // onUpdate={(e, value) => {
              //   if (value?.name !== 'Others') {
              //     setValue(`order_details.${index}.notes`, '')
              //   }
              // }}
              />
            </TableCell>
          </>
        )}
      </>
    )
  }

  const getDocument = (ind) => {
    const hasDocuments = viewOrderDialog.data.some(item => item.documents);
    const eachHasDocument = viewOrderDialog.data.every(item => item.documents);
    const packingSlip = viewOrderDialog.data[ind]?.packing_slips
    const supplierInvoices = viewOrderDialog.data[ind]?.supplier_invoices

    if (hasDocuments ||  viewOrderDialog.stage === 'invoice') {
      if (viewOrderDialog.data[ind].documents || packingSlip?.length > 0 || supplierInvoices?.length > 0) {
        // setOpen((prev) => ({ [ind]: true }))}
        return <TableCell scope="row">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen((prev) => ({ ...prev, [ind]: !prev[ind] }))}
            title="Show VAS"
          >
            <Icon>
              {open[ind] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </Icon>
          </IconButton>
        </TableCell>
      }
      return <TableCell />
    }
    if (eachHasDocument || viewOrderDialog?.stage === 'invoice') {
      if (viewOrderDialog.data[ind].documents || viewOrderDialog.data[ind].vas) {
        return <TableCell scope="row">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen((prev) => ({ ...prev, [ind]: !prev[ind] }))}
            title="Show VAS"
          >
            <Icon>
              {open[ind] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </Icon>
          </IconButton>
        </TableCell>
      }
    }
    if (getType('Receive Order'))
      return <TableCell><></></TableCell>
    return ""

  }

  /**
  * Accordion toggle handle
  */
  const handleToggle = (accordionKey) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionKey]: isExpanded,
    }));
  };

  const printFieldDisplayDetails = {
    showOrderNo: ['invoice', 'in-production'].includes(viewOrderDialog.stage) || isOutboundShipments,
    showWeight: (viewOrderDialog.stage !== 'invoice' && !isColumnHidden('Weight')) || isOutboundShipments,
    showTotalWeight: (viewOrderDialog.stage !== 'invoice' && !isColumnHidden('Total Weight')) || isOutboundShipments,
    showBoxNo: (['in-production', 'fulfilled','container-management'].includes(viewOrderDialog.stage)) || ['invoice'].includes(viewOrderDialog.stage) && isSupplier(currentUser.role),
    showCustomInventoryType: isOutboundShipments,
    showDiscount: !hideDiscountField(),
    showPrice:!isColumnHidden('Price'),
    showAmount:!isColumnHidden('Amount'),
    showCustomerName: showCustomerNamefield(),
    showOrderType: showOrderTypefield(),
    showReferenceNo: !isColumnHidden('Reference No'),
    showDecedent: !isColumnHidden('Decedent'),
    showComments: !isColumnHidden('Comments'),
    showVasPrice: showVasPriceDetails()
  }
  const showEmptySupplierAlert = isCommunicationTab && !Array.isArray(list) || list?.length === 0 || list?.every(item => item === null)

  return viewOrderDialog && viewOrderDialog.props && viewOrderDialog.props.open ? (
    <FormProvider {...methods}>
      <AuraBaseDialog
        dialogProps={viewOrderDialog.props}
        closeDialog={closeDialog}
        dialogParam={{
          title: viewOrderDialog.param.title,
          hideDialogActions: !(getType('send to supplier') || getType('create supplier order') || getType('create supplier order')
            || viewOrderDialog?.param?.onSubmit || getType('receive shipment')
          ) || showEmptySupplierAlert,
          maxWidth: viewOrderDialog.param.maxWidth || 'xl',
          submitButtonName: viewOrderDialog.param.submitButtonName,
          type: viewOrderDialog.param.type,
        }}
        disableSubmitButton={disableSubmitButton}
        header={headerData}
        onSubmit={handleSubmit(() => handleFormSubmit())}
        showDialogActions={viewOrderDialog?.param?.showDialogActions ? getComponent() : null}
        additionalButton={additionalButton}
        disableCloseIcon={loading || performingAction}
      >
        {suppliersListLoading || quotedSuppliersListLoading || performingAction || loading ? (
          <Loader />
        ) : (
          <>
            {viewOrderDialog.stage === 'invoice' && canViewInvoiceDiscrepancy && completeOrderData?.id && invoiceDiscrepancy && (
              <div className="print:hidden mb-16">
                <InvoiceDiscrepancyPanel
                  invoiceId={completeOrderData.id}
                  discrepancy={invoiceDiscrepancy}
                  compact
                  readOnly
                />
              </div>
            )}
            <div className="hidden">
              <PrintableColumns
                header={headerData}
                data={viewOrderDialog.data}
                ref={printRef}
                showDiscount={!hideDiscountField()}
                fields={printFieldDisplayDetails}
                isPrinting={isPrinting}
              />
            </div>
            {isDialogName('viewOrder') && (
              <div className="table-responsive w-full">
                {viewOrderDialog?.param?.title === "Create Supplier Order" && (
                  <div>
                    <Checkbox
                      checked={checked}
                      className='px-8 py-8'
                      onChange={() => { setChecked(!checked) }}
                    />
                    Send for Approval
                  </div>
                )}
                {getType('send to supplier') && (
                  <>
                    {previousSuppliers?.length > 0 &&
                      <div className='mb-10'><Typography variant="h6" className="mt-6">Previously Contacted Suppliers:</Typography>
                        {
                          previousSuppliers?.map((item) => (
                            <Chip className='m-6' label={item.name} variant='outlined' />
                          ))
                        }</div>
                    }
                    {
                      showEmptySupplierAlert ? (
                        <Alert severity="info">{`Selected ${viewOrderDialog.stage} is not associated with any of the Suppliers.`}</Alert>
                      ) : (
                        <>
                          <Typography variant="h6" className="mt-6 mb-6">Select Suppliers:</Typography>
                          <AuraControlledAutocomplete
                            labelName='Suppliers'
                            control={control}
                            controlName='suppliers'
                            inputValues={list || []}
                            onUpdate={(e, value) => {
                              setSelectedSuppliers(value);
                              setIschnaged(true)
                            }}
                            multiple
                            className='mt-6 mb-24 '
                            required
                          />
                          {/*
                            PGSS-5331 / PGSS-5318: staff's note to the supplier for this send. Optional —
                            leaving it empty means the supplier is shown no note at all, which is the point:
                            they used to be shown whatever the previous round left behind (PGSS-5332).
                            Separate from the customer-side revision note, which stays customer-side.

                            PGSS-5318: the prompt follows the round. This dialog opens on every send, and on
                            an initial RFQ it was asking why the supplier is being asked to quote again when
                            they have not quoted at all yet.
                          */}
                          <TextField
                            {...supplierNoteFieldCopy(
                              roundHandoffLoaded
                                ? isRepeatSupplierRound({ previousSuppliers, hasRoundHandoff })
                                : null
                            )}
                            value={supplierNote}
                            onChange={(e) => setSupplierNote(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                            variant="outlined"
                            className="mb-24"
                          />
                          {/*
                            PGSS-5331: the documents this round attached, all included to start with.
                            Untick one and it is not sent. Shown here because this is the only screen where
                            staff decide what the supplier receives — the files live on the customer order
                            and reach the supplier by no other route (PGSS-5289).
                          */}
                          {roundDocuments.length > 0 && (
                            <div className="mb-24">
                              <Typography variant="h6" className="mb-6">Documents to send:</Typography>
                              {roundDocuments.map((document) => {
                                /*
                                  filename first, name second — the order every other attachment list uses
                                  (ImageContent, CheckList, AuraFunctions.download). Both come off the file
                                  row, but name is frequently empty while filename is what was uploaded, so
                                  reading name alone renders a tickable row with no label on it.
                                */
                                const documentLabel = document?.filename || document?.name || 'Untitled document';
                                /*
                                  PGSS-5318: a filename and a tick box is not enough to decide with. Staff
                                  are answering "does this file go to the supplier?", and the point of
                                  composing here is that they see exactly what the supplier gets — which
                                  they cannot do without opening it. View for what the browser can render,
                                  Download for the rest; both need a source on the row, so a hand-off that
                                  returns bare metadata still lists and ticks as before.
                                */
                                const isOpenable = Boolean(document?.url || document?.data);
                                return (
                                  <div key={document.id} className="flex items-center">
                                    <Checkbox
                                      className="px-8 py-4"
                                      checked={selectedRoundDocumentIds.includes(document.id)}
                                      onChange={() =>
                                        setSelectedRoundDocumentIds((current) =>
                                          current.includes(document.id)
                                            ? current.filter((id) => id !== document.id)
                                            : [...current, document.id]
                                        )
                                      }
                                      inputProps={{ 'aria-label': `Send ${documentLabel} to the supplier` }}
                                    />
                                    <Typography>{documentLabel}</Typography>
                                    {isOpenable && (
                                      <>
                                        {canViewFile(document) && (
                                          <FileAction label="View" onActivate={() => onFileViwerClick(document)} />
                                        )}
                                        <FileAction label="Download" onActivate={() => handleDownload(document)} />
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                              <Typography className="text-11 text-grey-600 mt-4">
                                Only ticked documents are sent to the supplier.
                              </Typography>
                            </div>
                          )}
                        </>
                      )
                    }
                  </>
                )}
                {!isCommunicationTab && <Table className="simple w-full min-w-full">
                  <AuraSimpleTableHead
                    headerList={headers}
                    classes={{ cnCell: 'font-semibold px-8 py-8' }}
                  />
                  <TableBody>
                    {getCols() || viewOrderDialog?.data?.map((orderDetail, index) => {
                      const proofDocuments = [];
                      let drawingDocuments = [];
                      const documentData = getValues()?.order_details?.[index]
                      const approvalDocuments = documentData?.approval_documents || []
                      const existDocuments = viewOrderDialog.param.is_proof_approval ? documentData?.proof_documents : documentData?.drawing_documents
                      const newDocuments = viewOrderDialog.param.is_proof_approval ? documentData?.new_proof_documents : documentData?.new_drawing_documents

                      let fDocuments = []
                      if (existDocuments?.length > 0) {
                        fDocuments = [...fDocuments, ...existDocuments]
                      }
                      if (newDocuments?.length > 0) {
                        fDocuments = [...fDocuments, ...newDocuments]
                      }
                      if (fDocuments?.length > 0) {
                        fDocuments?.map(d => {
                          if (d.image_customisation_type === 'proof' || viewOrderDialog.param.is_proof_approval)
                            return proofDocuments.push(d)
                          return drawingDocuments.push(d)
                        })
                      }

                      if (!getValues()?.order_details || documentData?.showDocuments) {
                        if (orderDetail?.drawing_documents?.length > 0 && !orderDetail?.proof_documents) {
                          drawingDocuments = orderDetail?.drawing_documents;
                        }
                      }

                      if (approvalDocuments?.length > 0) {
                        drawingDocuments = approvalDocuments
                      }

                      const formValues = getValues()?.order_details?.[index] || {}
                      const { isSelected } = formValues || {}

                      return <Fragment key={index}>
                        <TableRow>
                          {getDocument(index)}
                          <TableCell scope="row">
                            <Typography className="">{index + 1}</Typography>
                          </TableCell>
                          {((['invoice', 'in-production','container-management'].includes(viewOrderDialog.stage)) || isOutboundShipments) && <TableCell scope="row">
                            <Typography className="">{orderDetail.order_no}</Typography>
                          </TableCell>}
                          {/* TODO This is a temporary fix that need to be removed once invoice response has been added */}
                          {viewOrderDialog.stage === 'invoice' ? oldColumns(orderDetail) : newColumns(orderDetail, index)}
                        </TableRow>

                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan="100%">
                            <Collapse in={Boolean(open[index])} timeout="auto">
                              {drawingDocuments.length ?
                                <>
                                  <Typography variant="h6" className="mt-6 mb-10">
                                    {orderDetail.vas ? 'Proof' : 'Drawing'}
                                  </Typography>
                                  <ImageRenderer attachments={drawingDocuments} handleSelectionMedia={(media) => handleSelectedImages(media, index)} showCheckbox={['Send for Quote Approval', 'Send to Supplier'].includes(viewOrderDialog?.param?.title) || inlineInput === 'drawings'} />
                                </> : null
                              }
                            </Collapse>

                          </TableCell>
                        </TableRow>
                      </Fragment>
                    })}
                    {/* VAS price details section */}
                    {showVasPriceDetails() && (
                      <TableRow p={0}>
                        <TableCell colSpan="100%" className="!p-0">
                          <VasPriceList
                            type="view"
                            from="detail"
                            vasPriceDetailsData={
                              viewOrderDialog.data[0].vas_price_details
                            }
                            hideAmountDetails={isOutboundShipments || isStaffWithoutPriceView(currentUser?.role_view)}
                          />
                        </TableCell>
                      </TableRow>
                      )}

                    {viewOrderDialog.param?.packingSlips?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan="100%">
                          <Typography
                            variant="h6"
                            gutterBottom
                            component="div"
                            className="mt-6"
                          >
                            Packing Slips
                          </Typography>
                          <ImageRenderer attachments={viewOrderDialog.param?.packingSlips} />
                        </TableCell>
                      </TableRow>
                    )}

                    {viewOrderDialog.param?.supplierInvoices?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan="100%">
                          <Typography
                            variant="h6"
                            gutterBottom
                            component="div"
                            className="mt-6"
                          >
                            Supplier Invoices
                          </Typography>
                          <ImageRenderer attachments={viewOrderDialog.param?.supplierInvoices} />
                        </TableCell>
                      </TableRow>
                    )}

                    {viewOrderDialog?.data?.[0]?.uploadedFile && (
                      <TableRow>
                        <TableCell colSpan="100%" className='!px-0'>
                          <ImageContent
                            image={image}
                            title='Documents'
                            onChange={() => setMap(map !== 'documents' ? 'documents' : false)}
                            expanded={map === 'documents'}
                          />
                        </TableCell>
                      </TableRow>
                    )}

                    {(viewOrderDialog?.data?.[0]?.revision_note ||
                      completeOrderData?.revision_note ||
                      viewOrderDialog?.data?.[0]?.revision_documents) && (
                      <TableRow>
                        <TableCell colSpan="100%" className='!px-0'>
                          <div>
                            {(viewOrderDialog?.data?.[0]?.revision_note ||
                              completeOrderData?.revision_note) && (
                              <div className="flex flex-row bg-[#fcfcec] p-16 py-8 mb-8">
                                <Typography className="font-semibold text-[15px]">
                                  Revision Note:
                                </Typography>
                                <Typography className="ml-8 font-semibold text-red text-[15px]">
                                  {viewOrderDialog?.data?.[0]?.revision_note ||
                                    completeOrderData?.revision_note}
                                </Typography>
                              </div>
                            )}
                            {viewOrderDialog?.data?.[0]?.revision_documents && (
                            <AuraAccordion
                              title="Revision Documents"
                              isExpanded={expandedAccordions.revisionDocuments}
                              onChange={handleToggle("revisionDocuments")}
                              styles={accordionStyle}
                            >
                              <SimpleCardDocumentsViewer
                                attachments={viewOrderDialog?.data?.[0]?.revision_documents}
                                width="w-[12rem]"
                                height="h-[9rem]"
                                fontSize="text-[13px]"
                                />
                            </AuraAccordion>
                            )}
                            </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>

                </Table>}
                {addNote.includes(viewOrderDialog?.param.name)
                  && (<div className="flex flex-col md:flex-row w-full mt-10">
                    <div className="flex w-full ">
                      <Controller
                        control={control}
                        name="note"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mb-24"
                            label="Notes"
                            id="note"
                            variant="outlined"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            error={!!errors.note}
                            helperText={errors?.note?.message}
                          />
                        )}
                      />

                    </div>
                  </div>)
                }

              </div>
            )}
          </>
        )}
      </AuraBaseDialog >
      <ApprovalContent
        {...approvalDialog}
        onClose={() => setApprovalDialog({ open: false })}
        heading='Add Documents for Approval'
        hideDialogActions
      />
      <PaymentReversalDialog
        open={!!paymentToReverse}
        payment={paymentToReverse}
        // PGSS-5544: an ADVANCE_PAYMENT row carries no invoice_id -- transferForAdvancePayments
        // never sets one, because an advance is recorded against the order before any invoice
        // exists. Without this fallback the reversal posted to /api/invoice/null/... and 400'd.
        // InvoicePaymentsPanel has always had the same fallback; this dialog was missing it.
        invoiceId={paymentToReverse?.invoice_id ?? completeOrderData?.id}
        onClose={() => setPaymentToReverse(null)}
        onReversed={() => {
          // The list is what the operator judges the result by, so pull it fresh rather than
          // patching the row — the whole invoice's standing may have changed.
          if (completeOrderData?.id) {
            dispatch(getPaymentHistory(completeOrderData.id));
          }
        }}
      />
    </FormProvider >
  ) : (
    <></>
  );
}
